/**
 * Which Tixologi service owns a given path.
 *
 * The Tixologi backend is three Go services (Tixologi-Inc/local-sandbox/docker-compose.yml):
 * ticketing-api-2.0 (:8001), users-api-2.0 (:8000), notifications-api (:8002). In
 * development they are three hosts; in preview/production they sit behind one
 * path-routing gateway, which is why a single NEXT_PUBLIC_TIXOLOGI_API_URL has sufficed.
 *
 * THE TRAP this module exists to prevent: point NEXT_PUBLIC_TIXOLOGI_API_URL at a raw
 * ticketing Cloud Run preview and every users-api /partners/* call 404s. ticketing-api
 * registers no /login, /users or /members route and, under /partners, only the single
 * path listed in TICKETING_OWNED_USERS_SHAPED_PATHS; it installs no gin NoRoute handler,
 * so the failure is a bare, bodiless 404 that reads as a broken dashboard rather than a
 * misconfiguration.
 *
 * Full topology, ports and route prefixes: packages/tixologi/CLAUDE.md.
 */

const PROD_GATEWAY = "https://api-v2.tixologi.com";
const STAGING_GATEWAY = "https://api-test-v2.tixologi.com";

/**
 * The gateway a deployment resolves to with nothing configured.
 *
 * This is the literal that used to be hardcoded, twice, in
 * app/(public)/api/analyticsVenue{EventList,SalesData}/utils.ts. Deliberately NOT the
 * terminal of the client-facing resolver below: a missing NEXT_PUBLIC_TIXOLOGI_API_URL
 * must keep failing loudly rather than silently serving staging data to production.
 */
function defaultTixologiGateway(): string {
  return process.env.VERCEL_ENV === "production"
    ? PROD_GATEWAY
    : STAGING_GATEWAY;
}

/**
 * Paths owned by users-api.
 *
 * Narrower than tixologist's interceptor (Tixologi-Inc/tixologist/src/services/api.ts),
 * which uses an unanchored `['login','users','partners','members'].some(w =>
 * url.includes(w))` — that also matches the substring inside e.g. `/promotions_users`.
 * This pattern requires whole path SEGMENTS.
 *
 * CAVEAT — it is a segment heuristic, not a route table, and it is NOT anchored to the
 * FIRST segment: `/events/1/users/2/tickets` (a real ticketing route,
 * ticketing-api-2.0 cmd/api/router.go) matches on its `/users/` segment and would be
 * routed to users-api. No downstroke call site builds that shape today — every users
 * match in this repo is a genuine users-api path — but add an explicit exclusion here
 * before adding one. Do NOT "fix" this by switching to first-segment routing: /punchup
 * is owned by BOTH ticketing (/punchup/events*) and users (/punchup/login), and /public
 * by all three.
 */
const USERS_API_PATH_PATTERN = /\/(login|users|partners|members)(\/|$|\?)/;

/**
 * Ticketing-owned paths that LOOK like users-api paths to the pattern above.
 *
 * ticketing-api-2.0 mounts the venue box office fee under /partners (cmd/api/router.go,
 * TIC-889) because the fee is a partner-level setting, not an event one. The production
 * gateway carries a matching path rule that sends exactly this path to ticketing ahead of
 * its /partners/* → users-api rule, so the shared-gateway case was already correct; this
 * list is what keeps the cross-env preview correct, where users paths are pinned to a
 * different host than ticketing. Match on the path with any query string stripped.
 *
 * Entries are EXACT paths, not prefixes: a sub-route such as `/partners/box-office-fee/{id}`
 * still resolves to users-api. Add each new ticketing-owned path here as its own entry,
 * together with its gateway path rule.
 */
const TICKETING_OWNED_USERS_SHAPED_PATHS = new Set([
  "/partners/box-office-fee",
]);

function isUsersApiPath(path: string): boolean {
  const pathname = path.split("?")[0];
  if (TICKETING_OWNED_USERS_SHAPED_PATHS.has(pathname)) return false;
  return USERS_API_PATH_PATTERN.test(path);
}

/**
 * Absolute URL for a Tixologi path, routed to the service that owns it.
 *
 * With NEXT_PUBLIC_TIXOLOGI_USERS_API_URL unset this is byte-identical to interpolating
 * NEXT_PUBLIC_TIXOLOGI_API_URL directly — including the `undefined/...` string that a
 * misconfigured environment produces today.
 *
 * NEXT_PUBLIC_TIXOLOGI_USERS_API_URL is the users-api service ROOT. It is NOT
 * TIXOLOGI_USERS_URL: that one is server-only, carries a `/punchup` suffix, and is the
 * LOGIN base used by apps/web/lib/tixologi-token.ts. Copying one into the other yields
 * `/punchup/punchup/login`.
 */
export function tixologiUrl(path: string): string {
  const base = isUsersApiPath(path)
    ? process.env.NEXT_PUBLIC_TIXOLOGI_USERS_API_URL ||
      process.env.NEXT_PUBLIC_TIXOLOGI_API_URL
    : process.env.NEXT_PUBLIC_TIXOLOGI_API_URL;
  return `${base}${path}`;
}

/**
 * Base URL of ticketing-api-2.0 for the twice-hourly analytics crons
 * (app/(public)/api/analyticsVenue{EventList,SalesData}). SERVER ONLY.
 *
 * Deliberately does NOT chain through TIXOLOGI_API_BASE_URL or
 * NEXT_PUBLIC_TIXOLOGI_API_URL. These routes do not merely read: a 200 from the WRONG
 * host runs deleteStaleEvents on venue_tixologi_events (and, with zero events returned,
 * deletes every row for the partner) plus delete_venue_sales_data + re-insert on
 * venue_sales_data_raw. Keeping the chain to one dedicated, never-configured name makes
 * "this deploy resolves what it resolved yesterday" provable from the diff alone.
 */
export function analyticsTicketingApiBase(): string {
  return process.env.TIXOLOGI_ANALYTICS_API_URL || defaultTixologiGateway();
}
